import {areWeTestingWithJest, isDevEnv} from "@buildwithflux/shared";
import {StoreApi, UseBoundStore, create} from "zustand";
import {devtools} from "zustand/middleware";

import {useFluxServices} from "injection/hooks";

/**
 * State for the current user's permissions on the current document
 *
 * This replaces the Redux auth reducer state, which tracked whether the current user
 * has commenting and editing permissions on the currently loaded document.
 */
export type AuthPermissionsStoreState = {
    /**
     * Whether the current user has permission to comment on the current document
     */
    currentUserHasCommentingPermission: boolean;

    /**
     * Whether the current user has permission to edit the current document
     */
    currentUserHasEditPermission: boolean;

    /**
     * Sets the commenting permission for the current user
     */
    setCurrentUserHasCommentingPermission: (hasCommentingPermission: boolean) => void;

    /**
     * Sets the edit permission for the current user
     */
    setCurrentUserHasEditPermission: (hasEditPermission: boolean) => void;

    /**
     * Resets permissions to their initial state (both false)
     * Useful when navigating away from a document
     */
    resetPermissions: () => void;
};

/**
 * The exported type of the store
 */
export type UseAuthPermissionsStore = UseBoundStore<StoreApi<AuthPermissionsStoreState>>;

/**
 * Creates the vanilla non-bound-to-React auth permissions store
 *
 * This is a vanilla Zustand store, and is only exported for use in tests. In components, you'd usually use the hooks
 * below, and even when trying to access low-level details of the store, you'd use the bound version of this store.
 *
 * The store returned from this function cannot be directly used as a hook in a component. For that, see
 * the hooks below.
 */
export function createAuthPermissionsStore(): UseAuthPermissionsStore {
    return create<AuthPermissionsStoreState>()(
        devtools(
            (set): AuthPermissionsStoreState => {
                return {
                    currentUserHasCommentingPermission: false,
                    currentUserHasEditPermission: false,

                    setCurrentUserHasCommentingPermission: (hasCommentingPermission: boolean) => {
                        set(
                            {currentUserHasCommentingPermission: hasCommentingPermission},
                            false,
                            "setCurrentUserHasCommentingPermission",
                        );
                    },

                    setCurrentUserHasEditPermission: (hasEditPermission: boolean) => {
                        set(
                            {currentUserHasEditPermission: hasEditPermission},
                            false,
                            "setCurrentUserHasEditPermission",
                        );
                    },

                    resetPermissions: () => {
                        set(
                            {
                                currentUserHasCommentingPermission: false,
                                currentUserHasEditPermission: false,
                            },
                            false,
                            "resetPermissions",
                        );
                    },
                };
            },
            {enabled: isDevEnv() && !areWeTestingWithJest(), name: "AuthPermissionsStore"},
        ),
    );
}

/**
 * Gets a reactive slice of the auth permissions store: whether the current user has commenting permission
 *
 * This is reactive in the sense that if you call this hook from a component, it'll re-execute if the value
 * of that piece of state changes (similar to if you called a useState setter)
 */
export function useHasCommentingPermission(): boolean {
    return useFluxServices().useAuthPermissionsStore((state) => state.currentUserHasCommentingPermission);
}

/**
 * Gets a reactive slice of the auth permissions store: whether the current user has edit permission
 *
 * This is reactive in the sense that if you call this hook from a component, it'll re-execute if the value
 * of that piece of state changes (similar to if you called a useState setter)
 */
export function useHasEditPermission(): boolean {
    return useFluxServices().useAuthPermissionsStore((state) => state.currentUserHasEditPermission);
}

/**
 * Returns the function to set the commenting permission
 */
export function useSetCommentingPermission(): (hasCommentingPermission: boolean) => void {
    return useFluxServices().useAuthPermissionsStore((state) => state.setCurrentUserHasCommentingPermission);
}

/**
 * Returns the function to set the edit permission
 */
export function useSetEditPermission(): (hasEditPermission: boolean) => void {
    return useFluxServices().useAuthPermissionsStore((state) => state.setCurrentUserHasEditPermission);
}

/**
 * Returns a getter function for the current edit permission value
 *
 * Use this hook when you need to read the edit permission imperatively inside callbacks or event handlers,
 * where you can't use the reactive `useHasEditPermission()` hook directly.
 *
 * @example
 * const getEditPermission = useGetEditPermission();
 * const handleClick = () => {
 *     if (getEditPermission()) {
 *         // do something
 *     }
 * };
 */
export function useGetEditPermission(): () => boolean {
    const {useAuthPermissionsStore} = useFluxServices();
    return () => useAuthPermissionsStore.getState().currentUserHasEditPermission;
}

/**
 * Hook that encapsulates the common edit permission logic.
 *
 * Returns true if the user can edit, which requires:
 * 1. The current user has edit permission on the document
 * 2. The application is not in version history mode
 * 3. Any additional component-specific checks pass (if provided)
 *
 * @param additionalChecks - Optional function that returns false if component-specific conditions prevent editing
 * @returns boolean indicating whether editing is allowed
 */
export function useCanEdit(additionalChecks?: () => boolean): boolean {
    const currentUserHasEditPermission = useHasEditPermission();
    const {useDocumentUiStore} = useFluxServices();
    const isVersionHistoryMode = useDocumentUiStore((state) => state.isVersionHistoryMode);

    if (!currentUserHasEditPermission) {
        return false;
    }
    if (isVersionHistoryMode) {
        return false;
    }
    if (additionalChecks && !additionalChecks()) {
        return false;
    }
    return true;
}
